import { defaultBeneficiaryAppSettings } from '@/components/helpers'
import { Organization, OrganizationChatSettings } from '@/store/user'
import { Button } from '@repo/ui/button'
import { H3 } from '@repo/ui/h3'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { Separator } from '@repo/ui/separator'
import { Cfl, onInput } from '@repo/ui/utils'
import { SaveAll } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

function formatColorValue(value: string | number[] | undefined): string {
  if (Array.isArray(value)) return value.join(',') ?? ''
  return value ?? ''
}

function getColorLabel(key: string) {
  return `${Cfl(key.replace('--color-', '').replace('-', ' '))} ${key.includes('gradient') ? '(RGB)' : '(Hex)'}`
}

const imageUrlSchema = z
  .string()
  .url()
  .refine((url) => /\.(jpg|jpeg|png|webp|svg)$/i.test(url), {
    message: 'Must be a valid image URL',
  })

export function BeneficiaryAppColors({
  handleSettings,
}: {
  handleSettings: {
    value: NonNullable<Organization['chatSettings']>
    onChange: (settings: OrganizationChatSettings) => void
  }
}) {
  const { tc, t } = useT({ keyPrefix: 'admin.organization.beneficiaryApp' })
  const [settings, setSettings] = useState<OrganizationChatSettings>(
    handleSettings.value,
  )

  const setColors =
    (key: keyof OrganizationChatSettings['colors']) => (color: string) => {
      setSettings((state) => ({
        ...state,
        colors: {
          ...state.colors,
          [key]: color,
        },
      }))
    }

  const setImageUrl = (imageUrl: string) => {
    setSettings((state) => ({
      ...state,
      imageUrl,
    }))
  }

  const isFormDisabled =
    Object.values(settings.colors).some((c) => !isValidColor(c)) ||
    (settings.imageUrl !== '' &&
      !imageUrlSchema.safeParse(settings.imageUrl).success)

  const update = () => {
    if (settings) handleSettings.onChange(settings)
  }

  return (
    <div className="mt-6 flex max-h-screen flex-col">
      <div className="flex flex-col gap-4 px-4 pb-6 xl:px-6">
        <H3 className="text-primary-500">{t('colors')}</H3>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(settings.colors).map(([key, value], index) => {
            const defaultValue =
              defaultBeneficiaryAppSettings.colors[
                key as keyof OrganizationChatSettings['colors']
              ]
            return (
              <div
                key={key}
                className={`flex flex-col gap-2 ${
                  index % 2 === 0 ? 'col-start-1' : 'col-start-2'
                }`}
              >
                <Label htmlFor={key} className="text-left">
                  {getColorLabel(key)}
                  <span className="ml-2 text-xs italic">
                    default: {formatColorValue(defaultValue)}
                  </span>
                </Label>
                <div
                  style={{
                    width: '100%',
                    height: 24,
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    marginBottom: 4,
                    background:
                      key.includes('gradient') &&
                      typeof value === 'string' &&
                      value.split(',').length === 3
                        ? `linear-gradient(90deg, rgb(${formatColorValue(value)}), rgb(${formatColorValue(value)}))`
                        : key.includes('gradient') && Array.isArray(value)
                          ? `rgb(${value.join(',')})`
                          : typeof value === 'string' && value.startsWith('#')
                            ? value
                            : typeof value === 'string' &&
                                value.split(',').length === 3
                              ? `rgb(${value})`
                              : '#fff',
                  }}
                />
                <Input
                  id={key}
                  value={formatColorValue(value)}
                  onInput={onInput(
                    setColors(key as keyof OrganizationChatSettings['colors']),
                  )}
                  placeholder={formatColorValue(defaultValue)}
                  maxLength={key.includes('gradient') ? 11 : 7}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div className="flex flex-col gap-4 px-4 pb-6">
        <H3 className="text-primary-500">{t('image')}</H3>
        <div className="flex flex-col gap-2">
          <Label htmlFor="imageUrl" className="text-left">
            {t('imageUrl')}
          </Label>
          <Input
            id="imageUrl"
            value={settings.imageUrl}
            onInput={onInput(setImageUrl)}
            placeholder={t('imageUrl')}
          />
        </div>
        <Separator className="my-4" />
        <Button onClick={update} disabled={isFormDisabled}>
          <SaveAll className="size-5" /> {tc('update', { cfl: true })}
        </Button>
      </div>
    </div>
  )
}

const isValidColor = (c: unknown) => {
  if (typeof c === 'string') {
    // Check for RGB: "num,num,num" where each num is 0-255
    const rgbRegex =
      /^\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*$/
    // Check for Hex: "#RRGGBB"
    const hexRegex = /^#[0-9A-Fa-f]{6}$/
    return rgbRegex.test(c) || hexRegex.test(c)
  }
  if (
    Array.isArray(c) &&
    c.length === 3 &&
    c.every((n) => typeof n === 'number' && n >= 0 && n <= 255)
  ) {
    return true
  }
  return false
}
